/**
 * La table d'indexation de Glougle — **la fonctionnalité centrale du skin**.
 *
 * C'est le seul skin qui parle des autres : il indexe les dix autres interfaces,
 * et la bonne requête y donne accès. Il fonctionne donc à la fois comme skin à
 * part entière et comme plan du site caché.
 *
 * Rien ici ne touche au DOM. La correspondance est une opération sur des
 * chaînes, et c'est là que vivent les deux pièges du handoff : la clé la plus
 * longue doit gagner, et le filtrage des suggestions doit laisser les amorces
 * de skin atteignables.
 */

import type { Leaf, Locale } from '../../i18n/strings';
import type { Skin } from '../../state/types';

/** Un résultat de l'index : de quoi rendre une carte complète. */
export interface IndexHit {
  readonly skin: Skin;
  /** Les deux jeux de clés sont actifs en même temps, quelle que soit la langue. */
  readonly keys: readonly string[];
  readonly bread: Leaf;
  readonly url: Leaf;
  readonly title: Leaf;
  readonly snippet: Leaf;
  /** Le libellé du bouton d'action — c'est lui qui porte l'information, pas le fond. */
  readonly cta: Leaf;
}

/*
 * Les noms de domaine inventés se traduisent quand ils portent du sens ; les
 * chemins techniques (`C:\`, `.bmp`, `.obj`) ne changent pas. Le résultat
 * `saas` reste **en anglais dans les deux langues** : c'est la blague de ce
 * skin-là.
 */
export const INDEX: readonly IndexHit[] = [
  {
    skin: 'aqua',
    keys: ['aquarium', 'bac', 'bassin', 'nourrir', 'os', 'tank', 'feed'],
    bread: { fr: 'AquariumOS', en: 'AquariumOS' },
    url: {
      fr: 'aquarium.os › bac › steve › moniteur',
      en: 'aquarium.os › tank › steve › monitor',
    },
    title: {
      fr: 'AquariumOS 1.4 — surveillance du bac en direct',
      en: 'AquariumOS 1.4 — live tank monitoring',
    },
    snippet: {
      fr: "Fenêtres empilables, journal du bac, thermomètre. Le pensionnaire est visible en permanence, sauf quand il est sorti. Compatible avec les bacs de toutes tailles.",
      en: 'Stackable windows, tank log, thermometer. The resident is visible at all times, except when he has left. Compatible with tanks of every size.',
    },
    cta: { fr: 'Ouvrir le bac', en: 'Open the tank' },
  },
  {
    skin: 'y2k',
    keys: [
      '1998',
      '2000',
      'netscape',
      'geocities',
      'vieux site',
      'ancien site',
      'nostalgie',
      'old site',
      'nostalgia',
    ],
    bread: {
      fr: 'Le Site Officiel du Poisson Steve',
      en: 'The Official Steve The Fish Site',
    },
    url: {
      fr: 'membres.multimania.fr › ~steve › index.htm',
      en: 'members.multimania.net › ~steve › index.htm',
    },
    title: {
      fr: 'BIENVENUE SUR LE SITE DU POISSON STEVE !!! ★彡',
      en: 'WELCOME 2 THE STEVE THE FISH SITE !!! ★彡',
    },
    snippet: {
      fr: "Meilleure résolution en 800×600. Ce site est optimisé pour Netscape Navigator 4. N'oubliez pas de signer le livre d'or ! Vous êtes le visiteur numéro 000147.",
      en: 'Best viewed at 800×600. This site is optimised for Netscape Navigator 4. Do not forget to sign the guestbook! You are visitor number 000147.',
    },
    cta: { fr: 'Entrer sur le site', en: 'Enter the site' },
  },
  {
    skin: 'saas',
    keys: [
      'startup',
      'saas',
      'b2b',
      'entreprise',
      'pro',
      'levée',
      'produit',
      'plateforme',
      'enterprise',
      'pricing',
      'platform',
    ],
    bread: { fr: 'Steve', en: 'Steve' },
    url: { fr: 'steve.io › platform', en: 'steve.io › platform' },
    title: {
      fr: 'Steve — The all-in-one aquatic locomotion platform',
      en: 'Steve — The all-in-one aquatic locomotion platform',
    },
    snippet: {
      fr: 'Trusted by 1 fish. Swim on land, walk in water, ship faster. Aucune carte bancaire requise, aucune carte bancaire acceptée. Voir les tarifs.',
      en: 'Trusted by 1 fish. Swim on land, walk in water, ship faster. No card required, no card accepted. See pricing.',
    },
    cta: { fr: 'Demander une démo', en: 'Book a demo' },
  },
  {
    skin: 'paint',
    keys: [
      'paint',
      'dessin',
      'dessine',
      'dessiné',
      'souris',
      'mspaint',
      'gribouill',
      'drawing',
      'doodle',
      'mouse',
    ],
    bread: { fr: 'sans titre', en: 'untitled' },
    url: {
      fr: 'C:\\Mes documents\\steve_final_FINAL(2).bmp',
      en: 'C:\\My Documents\\steve_final_FINAL(2).bmp',
    },
    title: {
      fr: 'steve_final_FINAL(2).bmp — Paint',
      en: 'steve_final_FINAL(2).bmp — Paint',
    },
    snippet: {
      fr: "Fichier non enregistré. Outils : pinceau, pot de peinture, aérographe, gomme. Le pot de peinture a débordé une fois et personne n'a annulé.",
      en: 'File not saved. Tools: brush, paint tin, airbrush, eraser. The paint tin overflowed once and nobody pressed undo.',
    },
    cta: { fr: 'Ouvrir dans Paint', en: 'Open in Paint' },
  },
  {
    skin: 'pixel',
    keys: [
      'pixel',
      '8 bit',
      '8bit',
      'jeu',
      'game',
      'arcade',
      'nes',
      'retro',
      'rétro',
      'console',
    ],
    bread: { fr: 'STEVE QUEST', en: 'STEVE QUEST' },
    url: { fr: 'cartouche.rom › steve › niveau-1', en: 'cartridge.rom › steve › level-1' },
    title: {
      fr: 'STEVE QUEST — 1 joueur, 3 vies, aucune fin connue',
      en: 'STEVE QUEST — 1 player, 3 lives, no known ending',
    },
    snippet: {
      fr: "INSÉREZ UNE PIÈCE. Le poisson traverse la terre à la nage. Aucun boss n'a jamais été atteint. Sauvegarde par mot de passe : ORANGE.",
      en: 'INSERT COIN. The fish swims across dry land. No boss has ever been reached. Password save: ORANGE.',
    },
    cta: { fr: 'Insérer une pièce', en: 'Insert coin' },
  },
  {
    skin: 'matrix',
    keys: [
      'matrix',
      'hacker',
      'terminal',
      'console',
      'shell',
      'code',
      'vert',
      'pirat',
      'green',
      'hack',
    ],
    bread: { fr: 'steve@matrix', en: 'steve@matrix' },
    url: { fr: 'root › tmp › poisson.sh', en: 'root › tmp › fish.sh' },
    title: {
      fr: '$ ./poisson --orange — session en cours',
      en: '$ ./fish --orange — session in progress',
    },
    snippet: {
      fr: "> analyse du sujet . . . poisson, orange, bipède > anomalie détectée : il a des bras. Le processus n'a pas été interrompu. Il ne s'interrompt jamais.",
      en: '> analysing subject . . . fish, orange, bipedal > anomaly detected: he has arms. The process was not interrupted. It never interrupts.',
    },
    cta: { fr: 'Ouvrir la session', en: 'Open the session' },
  },
  {
    skin: 'xp',
    keys: [
      'windows',
      'windowsxp',
      'xp',
      'bureau',
      'ordinateur',
      'pc',
      'démarrer',
      'demarrer',
      'colline',
      'prairie',
      'desktop',
      'computer',
      'start menu',
      'bliss',
      'hill',
    ],
    bread: { fr: 'Poste de travail', en: 'My Computer' },
    url: {
      fr: 'C:\\Documents and Settings\\Steve › Bureau',
      en: 'C:\\Documents and Settings\\Steve › Desktop',
    },
    title: {
      fr: 'Bureau — Steve (ordinateur familial)',
      en: 'Desktop — Steve (family computer)',
    },
    snippet: {
      fr: "Fond d'écran : une colline verte. Corbeille : pleine. Un assistant s'est ouvert et refuse de se fermer. Cliquez sur Démarrer pour commencer.",
      en: 'Wallpaper: a green hill. Recycle Bin: full. An assistant has opened and refuses to close. Click Start to begin.',
    },
    cta: { fr: 'Ouvrir le bureau', en: 'Open the desktop' },
  },
  {
    skin: 'skyblog',
    keys: [
      'skyblog',
      'blog',
      'kiff',
      'kikoo',
      'lache',
      'lâche',
      'com',
      'coms',
      'ado',
      'comments',
      '2007',
      'teen',
    ],
    bread: { fr: 'steve-officiel-59', en: 'steve-official-59' },
    url: {
      fr: 'steve-officiel-59.skyblog.com › article › 12',
      en: 'steve-official-59.skyblog.com › article › 12',
    },
    title: {
      fr: '★ le blog officiel de steve ★ lachez vos coms !!',
      en: '★ steve official blog ★ leave ur coms !!',
    },
    snippet: {
      fr: 'article n°12 posté le 04/04/2025. mercii pour tous vos coms sa fait trop plaisir. le prochain article c encore sur lui dsl pas dsl. 147 coms.',
      en: 'article no.12 posted 04/04/2025. thx 4 all ur coms it makes me so happy. next article is about him again sry not sry. 147 coms.',
    },
    cta: { fr: 'Lâcher un com', en: 'Leave a com' },
  },
  {
    skin: 'threed',
    keys: [
      '3d',
      'modele',
      'modèle',
      'blender',
      'obj',
      'glb',
      'maillage',
      'render',
      'rendu',
      'volume',
      'model',
      'mesh',
    ],
    bread: { fr: 'Steve.obj', en: 'Steve.obj' },
    url: { fr: 'atelier3d.net › viewer › steve', en: 'studio3d.net › viewer › steve' },
    title: {
      fr: 'Steve.obj — modèle 3D interactif (12 486 polygones)',
      en: 'Steve.obj — interactive 3D model (12,486 polygons)',
    },
    snippet: {
      fr: "Faites tourner le sujet. Matériau : orange, mat. Le maillage n'a jamais été nettoyé. Export OBJ et GLB disponibles, sans licence claire.",
      en: 'Rotate the subject. Material: orange, matte. The mesh has never been cleaned. OBJ and GLB export available, licence unclear.',
    },
    cta: { fr: 'Faire tourner le modèle', en: 'Rotate the model' },
  },
  {
    skin: 'wiki',
    keys: [
      'pedia',
      'wikipedia',
      'wikipédia',
      'encyclopédie',
      'encyclopedie',
      'poissonpédia',
      'poissonpedia',
      'fiche',
      'classification',
      'espèce',
      'espece',
      'encyclopedia',
      'fishipedia',
      'species',
    ],
    bread: { fr: 'Poissonpédia', en: 'Fishipedia' },
    url: {
      fr: 'poissonpedia.org › wiki › Le_poisson_Steve',
      en: 'fishipedia.org › wiki › Steve_the_fish',
    },
    title: {
      fr: "Le poisson Steve — Poissonpédia, l'encyclopédie à un seul article",
      en: 'Steve the fish — Fishipedia, the one-article encyclopedia',
    },
    snippet: {
      fr: "Article sourcé par une chanson de trente secondes. Trois bandeaux de maintenance. Treize révisions, aucune ne l'ayant rendu moins orange.",
      en: 'Article sourced to a thirty-second song. Three maintenance notices. Thirteen revisions, none of which made him less orange.',
    },
    cta: { fr: "Lire l'article", en: 'Read the article' },
  },
];

/**
 * Le skin que désigne une requête, s'il y en a un.
 *
 * **La clé la plus longue gagne.** Sans cette règle, « steve pixel art »
 * pourrait tomber sur `os` (deux lettres, contenues dans des dizaines de mots)
 * plutôt que sur `pixel`, et « geocities » se ferait battre par `code`. La
 * comparaison est un simple `includes`, jamais une correspondance floue : la
 * découverte doit rester nette, un visiteur doit pouvoir refaire exprès ce
 * qu'il vient de trouver par hasard.
 */
export function lookup(query: string): IndexHit | null {
  const q = query.toLowerCase();
  let best: IndexHit | null = null;
  let bestLength = 0;
  for (const hit of INDEX) {
    for (const key of hit.keys) {
      if (key.length > bestLength && q.includes(key)) {
        best = hit;
        bestLength = key.length;
      }
    }
  }
  return best;
}

/** Nombre de suggestions affichées sous la barre. */
export const SUGGESTIONS_SHOWN = 6;

/**
 * Les suggestions à montrer pour une saisie donnée.
 *
 * C'est **la seule piste** qui mène à l'index : rien dans l'interface n'explique
 * le mécanisme, et rien ne doit l'expliquer. Un visiteur qui tape « steve » voit
 * plusieurs amorces de skin d'un coup, et comprend seul en une frappe.
 *
 * Trois règles, et chacune correspond à une façon de tout casser :
 *
 * - **`includes`, pas l'égalité.** Un filtrage strict ne laisserait apparaître
 *   une amorce qu'une fois tapée en entier, c'est-à-dire jamais.
 * - **Les amorces d'abord, la coupe ensuite.** Couper à six avant de trier les
 *   remonte au-delà de la liste visible : la fonctionnalité existe encore mais
 *   personne ne la trouve.
 * - **Le menu ne se vide jamais.** Sans candidat, on rend tout : une liste vide
 *   ressemble à une panne.
 */
export function suggest(
  input: string,
  all: readonly string[],
  primers: readonly string[],
): readonly string[] {
  const typed = input.trim().toLowerCase();
  const matching = all.filter(
    (item) => item.toLowerCase().includes(typed) && item.toLowerCase() !== typed,
  );
  const pool = matching.length > 0 ? matching : all;
  const isPrimer = (item: string): boolean => primers.includes(item);
  return [...pool.filter(isPrimer), ...pool.filter((item) => !isPrimer(item))].slice(
    0,
    SUGGESTIONS_SHOWN,
  );
}

/** Ce que le message flottant dit d'une requête. */
export type SayKind = 'empty' | 'exact' | 'no_steve' | 'same';

/**
 * Le message flottant qui accompagne une recherche.
 *
 * Il n'explique jamais le mécanisme — il constate. « 1 résultat exact » suffit à
 * signaler qu'il vient de se passer quelque chose de différent.
 */
export function sayFor(query: string, hit: IndexHit | null): SayKind {
  if (query.trim() === '') return 'empty';
  if (hit) return 'exact';
  return /steve/i.test(query) ? 'same' : 'no_steve';
}

/** Découpe un extrait pour mettre en gras les mots de la requête. */
export interface Piece {
  readonly text: string;
  readonly hit: boolean;
}

/**
 * Met en gras, dans un texte, les mots de la requête.
 *
 * Les mots d'une lettre sont ignorés : en français, `à` et `y` surligneraient la
 * moitié des paroles et l'effet se retournerait.
 */
export function highlight(text: string, query: string): readonly Piece[] {
  const words = query
    .toLowerCase()
    .split(/\s+/)
    .filter((word) => word.length > 1);
  if (words.length === 0) return [{ text, hit: false }];

  const escaped = words.map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const pattern = new RegExp(`(${escaped.join('|')})`, 'gi');
  return text
    .split(pattern)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, hit: words.includes(part.toLowerCase()) }));
}

/** Le libellé traduit d'un champ de l'index. */
export function field(hit: IndexHit, key: 'bread' | 'url' | 'title' | 'snippet' | 'cta', lang: Locale): string {
  return hit[key][lang];
}
